import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import type { ImportResult } from 'hooks/api'
import { useBulkImportContacts } from 'hooks/api'
import { ArrowLeft, ArrowRight, CheckCircle, Plus, Trash2, Upload } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router'
import type { CsvParseResult, ParsedContactPreview } from 'utils/csv'
import { parseCsvForPreview } from 'utils/csv'
import type { SharedAssociation, SharedField } from 'utils/import'
import { KNOWN_CONTACT_FIELDS, previewToBulkContact } from 'utils/import'
type Step = 'upload' | 'review' | 'result'

const ASSOCIATION_KIND_LABELS: Record<SharedAssociation['kind'], string> = {
  schoolLocations: 'School Location',
  vendorLocations: 'Vendor Location',
  organizations: 'Organization',
}

const CONTACT_FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phoneNumber: 'Phone',
  organizationName: 'Organization',
  notes: 'Notes',
  ordoUserId: 'Ordo ID',
  timezone: 'Timezone',
}

function ColumnMappingSummary({ parsed }: { parsed: CsvParseResult }) {
  const mapped = parsed.mappedHeaders.filter((h) => !h.isExtra)
  const extra = parsed.mappedHeaders.filter((h) => h.isExtra)

  return (
    <div className="flex flex-wrap gap-2">
      {mapped.map((h) => (
        <Badge key={h.original} variant="secondary">
          {h.original} → {h.mapped}
        </Badge>
      ))}
      {extra.map((h) => (
        <Badge key={h.original} variant="outline" className="border-dashed">
          {h.original} → extra data
        </Badge>
      ))}
    </div>
  )
}

/* ── Shared Contact Fields Editor ── */

function SharedContactFieldsEditor({
  fields,
  onChange,
}: {
  fields: Record<string, string>
  onChange: (fields: Record<string, string>) => void
}) {
  const usedKeys = new Set(Object.keys(fields))
  const availableKeys = [...KNOWN_CONTACT_FIELDS].filter((k) => !usedKeys.has(k))

  const handleAdd = (key: string) => {
    onChange({ ...fields, [key]: '' })
  }

  const handleChange = (key: string, value: string) => {
    onChange({ ...fields, [key]: value })
  }

  const handleRemove = (key: string) => {
    const next = { ...fields }
    delete next[key]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">
          Shared contact fields <span className="font-normal">(applied when CSV row is empty)</span>
        </p>
        {availableKeys.length > 0 && (
          <Select onValueChange={handleAdd}>
            <SelectTrigger className="h-8 w-auto gap-1 text-xs">
              <Plus className="h-3 w-3" />
              <SelectValue placeholder="Add field" />
            </SelectTrigger>
            <SelectContent>
              {availableKeys.map((key) => (
                <SelectItem key={key} value={key}>
                  {CONTACT_FIELD_LABELS[key] ?? key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {Object.entries(fields).map(([key, value]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="w-28 shrink-0 text-sm text-muted-foreground">{CONTACT_FIELD_LABELS[key] ?? key}</span>
          <Input value={value} onChange={(e) => handleChange(key, e.target.value)} className="min-w-0 flex-1" />
          <Button variant="ghost" size="icon" onClick={() => handleRemove(key)} className="shrink-0">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
    </div>
  )
}

/* ── Shared Associations Editor ── */

function SharedAssociationsEditor({
  associations,
  onChange,
}: {
  associations: SharedAssociation[]
  onChange: (associations: SharedAssociation[]) => void
}) {
  const handleAdd = () => {
    onChange([...associations, { kind: 'schoolLocations', externalId: '', role: '' }])
  }

  const handleRemove = (index: number) => {
    onChange(associations.filter((_, i) => i !== index))
  }

  const handleChange = <K extends keyof SharedAssociation>(index: number, prop: K, val: SharedAssociation[K]) => {
    const updated = associations.map((a, i) => (i === index ? { ...a, [prop]: val } : a))
    onChange(updated)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">
          Shared associations <span className="font-normal">(attached to every contact)</span>
        </p>
        <Button variant="ghost" size="sm" onClick={handleAdd}>
          <Plus className="mr-1 h-3 w-3" />
          Add association
        </Button>
      </div>
      {associations.map((assoc, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <Select value={assoc.kind} onValueChange={(v) => handleChange(i, 'kind', v as SharedAssociation['kind'])}>
            <SelectTrigger className="h-9 w-40 shrink-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(ASSOCIATION_KIND_LABELS) as SharedAssociation['kind'][]).map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {ASSOCIATION_KIND_LABELS[kind]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="External ID"
            value={assoc.externalId}
            onChange={(e) => handleChange(i, 'externalId', e.target.value)}
            className="min-w-0 flex-1"
          />
          <Input
            placeholder="Role"
            value={assoc.role}
            onChange={(e) => handleChange(i, 'role', e.target.value)}
            className="min-w-0 flex-1"
          />
          <Button variant="ghost" size="icon" onClick={() => handleRemove(i)} className="shrink-0">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
    </div>
  )
}

/* ── Shared Extra Fields Editor ── */

function SharedExtraFieldsEditor({
  fields,
  onChange,
}: {
  fields: SharedField[]
  onChange: (fields: SharedField[]) => void
}) {
  const handleAdd = () => {
    onChange([...fields, { key: '', value: '' }])
  }

  const handleRemove = (index: number) => {
    onChange(fields.filter((_, i) => i !== index))
  }

  const handleChange = (index: number, prop: 'key' | 'value', val: string) => {
    const updated = fields.map((f, i) => (i === index ? { ...f, [prop]: val } : f))
    onChange(updated)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">
          Shared extra data <span className="font-normal">(custom key-value pairs)</span>
        </p>
        <Button variant="ghost" size="sm" onClick={handleAdd}>
          <Plus className="mr-1 h-3 w-3" />
          Add field
        </Button>
      </div>
      {fields.map((field, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            placeholder="Field name"
            value={field.key}
            onChange={(e) => handleChange(i, 'key', e.target.value)}
            className="min-w-0 flex-1"
          />
          <Input
            placeholder="Value"
            value={field.value}
            onChange={(e) => handleChange(i, 'value', e.target.value)}
            className="min-w-0 flex-1"
          />
          <Button variant="ghost" size="icon" onClick={() => handleRemove(i)} className="shrink-0">
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
    </div>
  )
}

/* ── Review Table ── */

function ReviewTable({
  contacts,
  mappedHeaders,
  excludedRows,
  excludedColumns,
  onToggleRow,
  onToggleAll,
  onToggleColumn,
  onCellEdit,
}: {
  contacts: ParsedContactPreview[]
  mappedHeaders: CsvParseResult['mappedHeaders']
  excludedRows: Set<number>
  excludedColumns: Set<string>
  onToggleRow: (index: number) => void
  onToggleAll: () => void
  onToggleColumn: (key: string) => void
  onCellEdit: (rowIndex: number, key: string, isExtra: boolean, value: string) => void
}) {
  const [editingCell, setEditingCell] = useState<{ row: number; key: string } | null>(null)
  const extraHeaders = mappedHeaders.filter((h) => h.isExtra)

  const columns: { label: string; key: string; isExtra: boolean }[] = [
    { label: 'First Name', key: 'firstName', isExtra: false },
    { label: 'Last Name', key: 'lastName', isExtra: false },
    { label: 'Email', key: 'email', isExtra: false },
    { label: 'Phone', key: 'phoneNumber', isExtra: false },
    { label: 'Organization', key: 'organizationName', isExtra: false },
    { label: 'Timezone', key: 'timezone', isExtra: false },
    { label: 'Notes', key: 'notes', isExtra: false },
    ...extraHeaders.map((h) => ({ label: h.original, key: h.original, isExtra: true })),
  ]

  const visibleColumns = columns.filter((col) => {
    return contacts.some((c) => {
      if (col.isExtra) {
        return !!c.extraData?.[col.key]
      }
      return !!(c as Record<string, string | undefined>)[col.key]
    })
  })

  function getCellValue(contact: ParsedContactPreview, col: { key: string; isExtra: boolean }): string {
    if (col.isExtra) {
      return (contact.extraData?.[col.key] as string) ?? ''
    }
    return (contact as Record<string, string | undefined>)[col.key] ?? ''
  }

  const allIncluded = contacts.length > 0 && excludedRows.size === 0
  const someIncluded = excludedRows.size > 0 && excludedRows.size < contacts.length

  return (
    <div className="max-h-[70vh] overflow-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0">
          <tr className="border-b border-border bg-muted">
            <th className="px-2 py-2 text-left">
              <input
                type="checkbox"
                checked={allIncluded}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = someIncluded
                  }
                }}
                onChange={onToggleAll}
                className="accent-primary"
              />
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">#</th>
            {visibleColumns.map((col) => {
              const colExcluded = excludedColumns.has(col.key)
              return (
                <th
                  key={col.key}
                  className={`whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground ${colExcluded ? 'opacity-40' : ''}`}>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={!colExcluded}
                      onChange={() => onToggleColumn(col.key)}
                      className="accent-primary"
                    />
                    {col.label}
                    {col.isExtra && (
                      <Badge variant="secondary" className="text-[10px]">
                        extra
                      </Badge>
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact, i) => {
            const excluded = excludedRows.has(i)
            return (
              <tr key={i} className={`border-b border-border last:border-0 ${excluded ? 'opacity-40' : ''}`}>
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={!excluded}
                    onChange={() => onToggleRow(i)}
                    className="accent-primary"
                  />
                </td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{i + 1}</td>
                {visibleColumns.map((col) => {
                  const colExcluded = excludedColumns.has(col.key)
                  const isEditing = editingCell?.row === i && editingCell?.key === col.key && !colExcluded
                  const cellValue = getCellValue(contact, col)

                  if (isEditing) {
                    return (
                      <td key={col.key} className="px-1 py-0.5">
                        <Input
                          autoFocus
                          defaultValue={cellValue}
                          className="h-7 text-xs"
                          onBlur={(e) => {
                            if (e.target.value !== cellValue) {
                              onCellEdit(i, col.key, col.isExtra, e.target.value)
                            }
                            setEditingCell(null)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const target = e.target as HTMLInputElement
                              if (target.value !== cellValue) {
                                onCellEdit(i, col.key, col.isExtra, target.value)
                              }
                              setEditingCell(null)
                            } else if (e.key === 'Escape') {
                              setEditingCell(null)
                            }
                          }}
                        />
                      </td>
                    )
                  }

                  return (
                    <td
                      key={col.key}
                      className={`max-w-64 truncate px-3 py-1.5 text-xs ${colExcluded ? 'opacity-40' : 'cursor-text hover:bg-muted/30'}`}
                      onClick={colExcluded ? undefined : () => setEditingCell({ row: i, key: col.key })}>
                      {cellValue || <span className="text-muted-foreground/50">—</span>}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/* ── Main Import Component ── */

export function ContactImport() {
  const { perform: bulkImport, loading } = useBulkImportContacts()
  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState<string | null>(null)
  const [parsed, setParsed] = useState<CsvParseResult | null>(null)
  const [sharedContactFields, setSharedContactFields] = useState<Record<string, string>>({})
  const [sharedAssociations, setSharedAssociations] = useState<SharedAssociation[]>([])
  const [sharedExtraFields, setSharedExtraFields] = useState<SharedField[]>([])
  const [editableContacts, setEditableContacts] = useState<ParsedContactPreview[]>([])
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set())
  const [excludedColumns, setExcludedColumns] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }
    setFileName(file.name)
    setError(null)
    setParsed(null)

    file
      .text()
      .then((text) => {
        const r = parseCsvForPreview(text)
        setParsed(r)
        if (r.contacts.length === 0) {
          setError('No contacts found in CSV. Check that the file has a header row and at least one data row.')
        }
      })
      .catch(() => {
        setError('Failed to read file')
      })
  }, [])

  const handleReview = useCallback(() => {
    if (parsed && parsed.contacts.length > 0) {
      setEditableContacts([...parsed.contacts])
      setExcludedRows(new Set())
      setExcludedColumns(new Set())
      setStep('review')
    }
  }, [parsed])

  const handleBack = useCallback(() => {
    setStep('upload')
  }, [])

  const handleImport = useCallback(async () => {
    if (!parsed) {
      return
    }
    setError(null)
    try {
      // Combine shared contact fields and shared extra fields into a single SharedField array
      const allSharedFields: SharedField[] = [
        ...Object.entries(sharedContactFields).map(([key, value]) => ({ key, value })),
        ...sharedExtraFields,
      ]
      const contacts = editableContacts
        .filter((_, i) => !excludedRows.has(i))
        .map((c) => {
          if (excludedColumns.size === 0) {
            return c
          }
          const cleaned: ParsedContactPreview = {}
          for (const field of KNOWN_CONTACT_FIELDS) {
            if (!excludedColumns.has(field) && (c as Record<string, string | undefined>)[field]) {
              ;(cleaned as Record<string, string>)[field] = (c as Record<string, string | undefined>)[field]!
            }
          }
          if (c.extraData) {
            const extra: Record<string, string> = {}
            for (const [key, val] of Object.entries(c.extraData)) {
              if (!excludedColumns.has(key) && val) {
                extra[key] = val
              }
            }
            if (Object.keys(extra).length > 0) {
              cleaned.extraData = extra
            }
          }
          return cleaned
        })
        .map((c) => previewToBulkContact(c, allSharedFields, sharedAssociations))
      const res = await bulkImport(contacts)
      setResult(res)
      setStep('result')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    }
  }, [
    parsed,
    editableContacts,
    excludedRows,
    excludedColumns,
    sharedContactFields,
    sharedExtraFields,
    sharedAssociations,
    bulkImport,
  ])

  const handleReset = useCallback(() => {
    setStep('upload')
    setFileName(null)
    setParsed(null)
    setSharedContactFields({})
    setSharedAssociations([])
    setSharedExtraFields([])
    setEditableContacts([])
    setExcludedRows(new Set())
    setExcludedColumns(new Set())
    setResult(null)
    setError(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [])

  const handleToggleRow = useCallback((index: number) => {
    setExcludedRows((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setExcludedRows((prev) => {
      if (prev.size === 0) {
        return new Set(editableContacts.map((_, i) => i))
      }
      return new Set()
    })
  }, [editableContacts])

  const handleToggleColumn = useCallback((key: string) => {
    setExcludedColumns((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const handleCellEdit = useCallback((rowIndex: number, key: string, isExtra: boolean, value: string) => {
    setEditableContacts((prev) =>
      prev.map((contact, i) => {
        if (i !== rowIndex) {
          return contact
        }
        if (isExtra) {
          return { ...contact, extraData: { ...contact.extraData, [key]: value } }
        }
        return { ...contact, [key]: value }
      }),
    )
  }, [])

  const totalCount = editableContacts.length || parsed?.contacts.length || 0
  const effectiveCount = totalCount - excludedRows.size

  // Count all shared attributes for display
  const sharedContactFieldCount = Object.values(sharedContactFields).filter((v) => v).length
  const sharedAssociationCount = sharedAssociations.filter((a) => a.externalId && a.role).length
  const sharedExtraFieldCount = sharedExtraFields.filter((f) => f.key && f.value).length
  const hasSharedData = sharedContactFieldCount + sharedAssociationCount + sharedExtraFieldCount > 0

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/contacts">Back to Contacts</BackLink>
      </div>

      {/* Upload step */}
      {step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Import Contacts</CardTitle>
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to import contacts. Column names are matched flexibly — most common formats are
              supported. Unrecognized columns are stored as extra data. Duplicates are merged by Ordo User ID or by
              exact name + organization match.
            </p>
          </CardHeader>
          <Separator />
          <CardContent className="space-y-6 pt-6">
            <div className="space-y-2">
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
              />
              {fileName && parsed && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{fileName}</span> — {parsed.contacts.length} contact
                  {parsed.contacts.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {parsed && parsed.contacts.length > 0 && (
              <>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Column mapping</p>
                  <ColumnMappingSummary parsed={parsed} />
                </div>

                <Separator />

                <SharedContactFieldsEditor fields={sharedContactFields} onChange={setSharedContactFields} />
                <SharedAssociationsEditor associations={sharedAssociations} onChange={setSharedAssociations} />
                <SharedExtraFieldsEditor fields={sharedExtraFields} onChange={setSharedExtraFields} />

                <div className="flex justify-end">
                  <Button onClick={handleReview}>
                    Review {parsed.contacts.length} Contact{parsed.contacts.length !== 1 ? 's' : ''}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review step */}
      {step === 'review' && parsed && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">
                Review — {effectiveCount} of {totalCount} contact{totalCount !== 1 ? 's' : ''}
              </h2>
              <p className="text-sm text-muted-foreground">
                Click a cell to edit. Uncheck rows to exclude them from import.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleBack}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleImport} disabled={loading || effectiveCount === 0}>
                <Upload className="mr-1 h-4 w-4" />
                {loading ? 'Importing...' : `Import ${effectiveCount} Contact${effectiveCount !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>

          {hasSharedData && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground">Shared:</span>
              {Object.entries(sharedContactFields)
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <Badge key={k} variant="outline">
                    {CONTACT_FIELD_LABELS[k] ?? k} = {v}
                  </Badge>
                ))}
              {sharedAssociations
                .filter((a) => a.externalId && a.role)
                .map((a, i) => (
                  <Badge key={`assoc-${i}`} variant="outline" className="border-dashed">
                    {ASSOCIATION_KIND_LABELS[a.kind]}: {a.externalId} ({a.role})
                  </Badge>
                ))}
              {sharedExtraFields
                .filter((f) => f.key && f.value)
                .map((f) => (
                  <Badge key={`extra-${f.key}`} variant="secondary">
                    {f.key} = {f.value}
                  </Badge>
                ))}
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <ReviewTable
            contacts={editableContacts}
            mappedHeaders={parsed.mappedHeaders}
            excludedRows={excludedRows}
            excludedColumns={excludedColumns}
            onToggleRow={handleToggleRow}
            onToggleAll={handleToggleAll}
            onToggleColumn={handleToggleColumn}
            onCellEdit={handleCellEdit}
          />
        </div>
      )}

      {/* Result step */}
      {step === 'result' && result && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <CardTitle>Import Complete</CardTitle>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{result.total} processed</Badge>
              <Badge variant="secondary" className="bg-green-900/30 text-green-400">
                {result.created} created
              </Badge>
              <Badge variant="secondary" className="bg-blue-900/30 text-blue-400">
                {result.updated} merged
              </Badge>
            </div>

            {result.errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">{result.errors.length} error(s):</p>
                <ul className="max-h-48 overflow-y-auto text-sm text-destructive">
                  {result.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <Separator />

            <div className="flex items-center gap-2">
              <Button asChild>
                <Link to="/contacts">View Contacts</Link>
              </Button>
              <Button variant="outline" onClick={handleReset}>
                Import Another
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </Page>
  )
}
